import dayjs from 'dayjs'
import { pmsAccountTypes, providers } from 'merklebase-utils/commons'
import { derivativeOptionsTypes, derivativePositionTypes } from 'merklebase-utils/enums'
import { camelCaseToWords } from 'utils/formatter'
import { findTier } from 'utils/liquidityTiers'

export const mapBalance = ({
  balance,
  index,
  prices,
  liquidityTiers,
  accountsById,
  portfoliosById,
  totalAUM,
  instrumentsPrices,
}) => {
  return {
    id: index,
    accountName: accountsById?.[balance?.accountId]?.name,
    accountType: pmsAccountTypes?.[accountsById?.[balance?.accountId]?.accountTypeId]?.name,
    platform: Object.values(providers).find(
      (p) => p.ticker === accountsById?.[balance?.accountId]?.link?.providerTicker
    )?.name,
    portfolioName: portfoliosById?.[balance?.directPortfolioId]?.name,
    linkName: accountsById?.[balance?.accountId]?.link?.name,
    total: balance?.total,
    available: balance?.available,
    equity: balance?.equity,
    currencyName: balance?.currencyName,
    baseCurrency:
      instrumentsPrices.byInstrument?.[balance?.currencyName?.toUpperCase()]?.baseCurrency,
    walletId: balance?.walletId || null,
    usdValue:
      balance.netPosition ||
      (Number(prices.byCurrency[balance.currencyName.toUpperCase()]?.currentValue || 0) ||
        Number(
          instrumentsPrices.byInstrument?.[balance?.currencyName.toUpperCase()]?.currentValue || 0
        )) * balance?.total,
    quantity: balance?.total,
    currency: balance?.currencyName.toUpperCase(),
    isCollateral: balance?.isCollateral,
    liquidityTiers: findTier(
      prices.byCurrency,
      balance?.currencyName.toUpperCase(),
      liquidityTiers
    ),
    class: prices.byCurrency[balance?.currencyName.toUpperCase()]?.class,
    sector: prices.byCurrency[balance?.currencyName.toUpperCase()]?.sector,
    account: accountsById[balance?.accountId],
    portfolio: portfoliosById[balance?.portfolioId],
    directPortfolio: portfoliosById[balance?.directPortfolioId],
    aumPercent:
      ((Number(
        prices.byCurrency[balance?.currencyName.toUpperCase()]?.currentValue ??
          balance?.instrumentPrice ??
          0
      ) *
        balance?.total) /
        totalAUM) *
      100,
    typeId: balance?.typeId,
    type: balance?.typeName,
    insufficientCollaterals: balance?.insufficientCollaterals,
    totalAllocatedLinkedCollaterals: balance?.totalAllocatedLinkedCollaterals,
    avgPrice: balance?.avgPrice,
    leverage: Number(balance?.leverage ?? 1),
    settlementTicker: balance?.settlementTicker,
    quoteTicker: balance?.quoteTicker,
    unrealizedPnl: balance?.unrealizedPnl,
    realizedPnl: balance?.realizedPnl,
    totalPnl: balance?.totalPnl,
    baseTicker: balance?.baseTicker,
    instrumentPrice: balance?.instrumentPrice,
    estimatedLiquidationPrice: balance?.liquidationPrice,
    mgnMode: balance?.mgnMode || null,
    borrowed: balance?.borrowed ? Number(balance.borrowed) : null,
    borrowedCurrency: balance?.borrowedCurrency || null,
    margin: balance?.margin ? Number(balance.margin) : null,
    mmrRatio: balance?.mmrRatio ? Number(balance.mmrRatio) : null,
    mmrValue: balance?.mmrValue ? Number(balance.mmrValue) : null,
  }
}

export const mapComputedBalance = ({
  balance,
  index,
  prices,
  accountsById,
  instrumentsPrices,
}) => ({
  id: index,
  currencyName: balance?.currency,
  computedBalance: Number(balance?.computed_balance),
  computedUsdValue:
    balance.computed_net_position ||
    (Number(prices.byCurrency[balance.currency.toUpperCase()]?.currentValue || 0) ||
      Number(instrumentsPrices.byInstrument?.[balance.currency.toUpperCase()]?.currentValue || 0)) *
      balance.computed_balance,
  actualBalance: Number(balance.actual_balance),
  actualBalanceUsdValue:
    balance.actual_net_position ||
    (Number(prices.byCurrency[balance.currency.toUpperCase()]?.currentValue || 0) ||
      Number(instrumentsPrices.byInstrument?.[balance.currency.toUpperCase()]?.currentValue || 0)) *
      balance.actual_balance,
  difference: Number(balance.diff),
  differenceUSDValue:
    (Number(prices.byCurrency[balance?.currency?.toUpperCase()]?.currentValue || 0) ||
      Number(
        instrumentsPrices.byInstrument?.[balance?.currency?.toUpperCase()]?.currentValue || 0
      )) * balance?.diff,
  currency: balance?.currency,
  account: accountsById[balance?.account_id],
  baseCurrency: instrumentsPrices.byInstrument?.[balance?.currency?.toUpperCase()]?.baseCurrency,
})

export const mapPositions = ({ position, index, portfoliosById, totalAUM, accountsById, calculatePnlPercent = false }) => {
  return {
    id: index,
    accountId: position.account_id,
    accountName: accountsById?.[position?.account_id]?.name,
    accountType: pmsAccountTypes?.[accountsById?.[position?.account_id]?.accountTypeId]?.name,
    platform: Object.values(providers).find(
      (p) => p.ticker === accountsById?.[position?.account_id]?.link?.providerTicker
    )?.name,
    platformTicker: accountsById?.[position?.account_id]?.link?.providerTicker,
    portfolioName: portfoliosById?.[position?.direct_portfolio_id]?.name,
    linkName: accountsById?.[position?.account_id]?.link?.name,
    account: accountsById?.[position?.account_id],
    portfolioId: position?.portfolio_id,
    portfolio: portfoliosById?.[position?.portfolio_id],
    directPortfolio: portfoliosById?.[position?.direct_portfolio_id],
    directPortfolioId: position?.direct_portfolio_id,
    directPortfolioName: portfoliosById?.[position?.direct_portfolio_id]?.name,
    integrationId: accountsById?.[position?.account_id]?.link?.id,
    integrationName: accountsById?.[position?.account_id]?.link?.name,
    counterpartyId: position.counterparty_id,
    date: dayjs(position?.date).toDate(),
    avgPrice: position?.avg_price,
    costBasis: position?.last_wac != null ? Number(position.last_wac) : null,
    expiryDate: position?.expiry_date,
    isLive: !position.is_close,
    markPrice: position?.mark_price,
    delta: position?.delta,
    gamma: position?.gamma,
    theta: position?.theta,
    vega: position?.vega,
    rho: position?.rho,
    netPosition: position?.net_position,
    notionalValue: position?.notional_value,
    marketValue: position?.market_value,
    equity: position?.equity,
    deltaNetPosition: position?.delta_net_position || position?.net_position,
    deltaCoinSize: position?.delta_coin_size,
    gammaNetPosition: position?.gamma_net_position,
    gammaCoinSize: position?.gamma_coin_size,
    thetaNetPosition: position?.theta_net_position,
    thetaCoinSize: position?.theta_coin_size,
    vegaNetPosition: position?.vega_net_position,
    vegaCoinSize: position?.vega_coin_size,
    instrument: position?.instrument,
    side: position?.side,
    fee: position?.fee_usd,
    feeTicker: position?.fee_ticker,
    price: position?.price,
    optionType: {
      id: position?.option_type_id,
      name: Object.keys(derivativeOptionsTypes).find(
        (dot) => derivativeOptionsTypes[dot] === position?.option_type_id
      ),
    },
    positionType: position?.position_type,
    positionTypeId: position?.position_type_id,
    positionTypeName: !position?.position_type_id
      ? 'Spot'
      : camelCaseToWords(
          Object.keys(derivativePositionTypes).find(
            (dpt) => derivativePositionTypes[dpt] === position?.position_type_id
          ) || (position?.position_type === 'trade' ? 'spot' : 'fundingFees')
        ),
    dtd: position?.dtd,
    dtdCoinSize: position?.dtd_coin_size,
    dtdPercent: calculatePnlPercent && totalAUM ? (Number(position?.dtd || 0) / totalAUM) * 100 : position?.dtd_aum_percent,
    mtd: position?.mtd,
    mtdCoinSize: position?.mtd_coin_size,
    mtdPercent: calculatePnlPercent && totalAUM ? (Number(position?.mtd || 0) / totalAUM) * 100 : position?.mtd_aum_percent,
    qtd: position?.qtd,
    qtdCoinSize: position?.qtd_coin_size,
    qtdPercent: calculatePnlPercent && totalAUM ? (Number(position?.qtd || 0) / totalAUM) * 100 : position?.qtd_aum_percent,
    ytd: position?.ytd,
    ytdCoinSize: position?.ytd_coin_size,
    ytdPercent: calculatePnlPercent && totalAUM ? (Number(position?.ytd || 0) / totalAUM) * 100 : position?.ytd_aum_percent,
    ltd: position?.ltd,
    ltdCoinSize: position?.ltd_coin_size,
    ltdPercent: calculatePnlPercent && totalAUM ? (Number(position?.ltd || 0) / totalAUM) * 100 : position?.ltd_aum_percent,
    dtdPrice: position.dtd_price,
    mtdPrice: position.mtd_price,
    qtdPrice: position.qtd_price,
    ytdPrice: position.ytd_price,
    dtdPriceChange: ((position?.mark_price - position?.dtd_price) / position?.dtd_price) * 100,
    mtdPriceChange: ((position?.mark_price - position?.mtd_price) / position?.mtd_price) * 100,
    qtdPriceChange: ((position?.mark_price - position?.qtd_price) / position?.qtd_price) * 100,
    ytdPriceChange: ((position?.mark_price - position?.ytd_price) / position?.ytd_price) * 100,
    aum: totalAUM,
    aumPercent: totalAUM ? (Number(position?.net_position) / totalAUM) * 100 : null,
    deltaAUMPercent: totalAUM ? (Number(position?.delta_net_position) / totalAUM) * 100 : null,
    gammaAUMPercent: totalAUM ? (Number(position?.gamma_net_position) / totalAUM) * 100 : null,
    thetaAUMPercent: totalAUM ? (Number(position?.theta_net_position) / totalAUM) * 100 : null,
    vegaAUMPercent: totalAUM ? (Number(position?.vega_net_position) / totalAUM) * 100 : null,
    strategy: position?.strategy,
    positionInstrumentType: position?.position_instrument_type,
    type: position?.position_type,
    quantityTicker: position?.quantity_ticker,
    underlyingAsset: position?.underlying_asset,
    strikePrice: position?.strike_price,
    totalQuantity: Number(position?.quantity),
    positionsCount: Number(position?.positions_count),
    instrumentProvider: position?.instrument_provider,
    transactionType: position?.type_id,
    custodian: position?.custodian,
    exchange: position?.exchange,
    interestDtd: Number(position?.interest_dtd || 0),
    interestMtd: Number(position?.interest_mtd || 0),
    interestQtd: Number(position?.interest_qtd || 0),
    interestYtd: Number(position?.interest_ytd || 0),
    interestLtd: Number(position?.interest_ltd || 0),
    realizedDtd: Number(position?.realized_dtd || 0),
    realizedMtd: Number(position?.realized_mtd || 0),
    realizedQtd: Number(position?.realized_qtd || 0),
    realizedYtd: Number(position?.realized_ytd || 0),
    realizedLtd: Number(position?.realized_ltd || 0),
    unrealizedDtd: Number(position?.unrealized_dtd || 0),
    unrealizedMtd: Number(position?.unrealized_mtd || 0),
    unrealizedQtd: Number(position?.unrealized_qtd || 0),
    unrealizedYtd: Number(position?.unrealized_ytd || 0),
    unrealizedLtd: Number(position?.unrealized_ltd || 0),
    baseTicker: position?.base_ticker,
    settleDate: position?.settle_date,
    dealId: position?.deal_id,
    settlementStatus: position?.settlement_status || 'No Group',
    expiration: position?.expiration ? new Date(position?.expiration) : '',
    instrumentType: position?.instrument_type,
    assetType: position?.asset_type,
    assetClass: position?.asset_class,
    estimatedLiquidationPrice: position?.liquidationPrice,
    dtd_account_equity_percent: position?.dtd_account_equity_percent,
    mtd_account_equity_percent: position?.mtd_account_equity_percent,
    ytd_account_equity_percent: position?.ytd_account_equity_percent,
    ltd_account_equity_percent: position?.ltd_account_equity_percent,
  }
}

export const mapMarginBalance = ({ balance, index, prices, accountsById, portfoliosById }) => ({
  id: index,
  marginBalance: Number(balance?.margin_balance),
  initialMargin: Number(balance?.initial_margin),
  currencyName: balance?.currency_name,
  walletId: balance?.wallet_id,
  marginBalanceUsdValue:
    Number(prices.byCurrency[balance?.currency_name.toUpperCase()]?.currentValue || 0) *
    Number(balance?.margin_balance),
  initialUsdValue:
    Number(prices.byCurrency[balance?.currency_name.toUpperCase()]?.currentValue || 0) *
    Number(balance?.initial_margin),
  currency: balance?.currency_name.toUpperCase(),
  maintenanceMargin: Number(balance?.maintenance_margin),
  maintenanceMarginUsdValue:
    Number(prices.byCurrency[balance?.currency_name.toUpperCase()]?.currentValue || 0) *
    Number(balance?.maintenance_margin),
  used:
    ((Number(balance?.initial_margin) + Number(balance?.maintenance_margin)) /
      Number(balance?.margin_balance)) *
    100,
  account: accountsById[balance?.account_id],
  portfolio: portfoliosById[balance?.portfolio_id],
  directPortfolio: portfoliosById[balance?.direct_portfolio_id],
})
